import React, {useState, useContext, useEffect} from 'react'
import AuthContext from "../../../contexts/AuthContext";
import { Input, Button, Modal, List, Card, Flex, Result, DatePicker, Alert, Select, Radio } from "antd";
import moment from "moment-timezone";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { GoogleMap, Marker, useLoadScript, DirectionsRenderer, Autocomplete } from "@react-google-maps/api";
import Geocode from "react-geocode";
import createRoot from 'react-dom';
import location from "../../../assets/location.png";
import LoadingScreen from "../../../utils/LoadingScreen";
import { MdAdminPanelSettings, MdPermContactCalendar } from "react-icons/md";
import { CheckCircleTwoTone } from "@ant-design/icons";

const CreateAppTask = ({ open, onHide, onTaskAssigned }) => {
    const { role } = useContext(AuthContext);
    const [selectedAdmin, setSelectedAdmin] = useState('');
    const [selectedUser, setSelectedUser] = useState({});
    const [admin, setAdmin] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isTaskAssigned, setIsTaskAssigned] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [task, setTask] = useState('');
    const [description, setDescription] = useState('');
    const [lanes, setLanes] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [shift, setShift] = useState('day');
    const { TextArea } = Input;

    // New states for Road ID selection
    const [availableRoadIds, setAvailableRoadIds] = useState([]);
    const [selectedRoadIds, setSelectedRoadIds] = useState([]);
    const [loadingRoadIds, setLoadingRoadIds] = useState(false);
    useEffect(() => {
        const fetchWebUsers = async () => {
            if (!open || role !== 'Admin') return;
            setLoading(true);
            try {
                const token = JSON.parse(window.localStorage.getItem("token"));
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}task/list_mobileusers`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.data.users) {
                    throw new Error('Failed to fetch app users');
                }
                const formattedUsers = response.data.users.map(user => ({
                    label: `${user.name} (${user.unique_verification_code})`,
                    value: user.name,
                    unique_verification_code: user.unique_verification_code
                }));
                setUsers(formattedUsers);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchWebUsers();
    }, [open, role]);
    useEffect(() => {
        const fetchAdminName = async () => {
            if (!open || role !== 'Admin') return;
            setLoading(true);
            try {
                const token = JSON.parse(window.localStorage.getItem("token"));
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}task/get_user_info`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.data.result) {
                    throw new Error('Failed to fetch admin name');
                }
                const fetchedAdmin = response.data.result;
                setAdmin([fetchedAdmin]);
                setSelectedAdmin(fetchedAdmin);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAdminName();
    }, [open, role]);

    // Fetch available Road IDs from uploaded videos
    useEffect(() => {
        const fetchRoadIds = async () => {
            if (!open) return;
            setLoadingRoadIds(true);
            try {
                const token = JSON.parse(window.localStorage.getItem("token"));
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}dataUpload/get_video_list`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const videos = response.data.videos || response.data.result || response.data || [];

                // Extract unique Road IDs
                const roadIdsMap = {};
                videos.forEach(video => {
                    if (video.road_id && !roadIdsMap[video.road_id]) {
                        roadIdsMap[video.road_id] = {
                            road_id: video.road_id,
                            road_name: video.project_title || 'Unknown Road',
                            video_count: 0
                        };
                    }
                    if (video.road_id) {
                        roadIdsMap[video.road_id].video_count++;
                    }
                });

                setAvailableRoadIds(Object.values(roadIdsMap));
            } catch (error) {
                console.error('Error fetching road IDs:', error);
                setError('Failed to load available Road IDs');
            } finally {
                setLoadingRoadIds(false);
            }
        };

        fetchRoadIds();
    }, [open]);

    const assignTask = async () => {
        if (role !== 'Admin') {
            return;
        }
        setLoading(true);
        try {
            if (!selectedUser || !selectedUser.value || !selectedUser.unique_verification_code) {
                setError('Please select a user to assign the task.');
                return;
            }
            if (selectedRoadIds.length === 0) {
                setError('Please select at least one Road ID to process.');
                return;
            }
            if (!task) {
                setError('Please fill Task Title');
                return;
            }
            if (!description) {
                setError('Please fill Task Description')
                return;
            }
            if (!dueDate) {
                setError('Please fill Due Date')
                return;
            }
            const uid = uuidv4();
            const assignedByName = selectedAdmin ? selectedAdmin.name : '';
            const payload = {
                taskid: uid,
                assignedTo: selectedUser.value,
                assignedUserCode: selectedUser.unique_verification_code,
                road_ids: selectedRoadIds, // Array of selected Road IDs
                taskTitle: task,
                taskDescription: description,
                dueDate: dueDate,
                shift: shift,
                assignedBy: assignedByName,
            };
            const token = JSON.parse(window.localStorage.getItem("token"));
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}task/apptask_assign`, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            console.log('Task assigned successfully:', response.data);
            onTaskAssigned();
            setIsTaskAssigned(true);
            setTimeout(() => {
                setIsTaskAssigned(false);
                onHide();
                resetForm();
            }, 5000);
            setSelectedUser({});
            setSelectedAdmin('');
            setSelectedRoadIds([]);
            setTask('');
            setDescription('');
            setDueDate(null);
            setShift('day');
            setError(null);
        } catch (error) {
            setError(error.message);
            console.error('Error assigning task:', error);
        } finally {
            setLoading(false);
        }
    };
    const resetForm = () => {
        setSelectedUser({});
        setUsers([]);
        setSelectedAdmin('');
        setAdmin([]);
        setSelectedRoadIds([]);
        setTask('');
        setDescription('');
        setDueDate(null);
        setShift('day');
        setError(null);
        setLoading(false);
    };

    const filteredUsers = users.filter(user =>
        user.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
    Geocode.setApiKey(process.env.REACT_APP_GOOGLE_KEYS);
    const mapId = "c7ce1a914d921fb";
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEYS,
        libraries: ["places"],
    });
    const [startingAddress, setStartingAddress] = useState('');
    const [endingAddress, setEndingAddress] = useState('');
    const [currentLocation, setCurrentLocation] = useState({
        lat: 28.6566389,
        lng: 77.2180907,
    });
    const [mapCenter, setMapCenter] = useState(currentLocation);
    const [directionsResponse, setDirectionsResponse] = useState(null);
    const [startingPoint, setStartingPoint] = useState(null);
    const [endingPoint, setEndingPoint] = useState(null);
    const [lastFocusedInput, setLastFocusedInput] = useState(null);
    const onMapClick = async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        Geocode.fromLatLng(lat.toString(), lng.toString()).then(
            (response) => {
                const address = response.results[0].formatted_address;
                if (lastFocusedInput === 'starting') {
                    setStartingAddress(address);
                    setStartingPoint({ address, location: { lat, lng } });
                    setMapCenter({ lat, lng });
                } else if (lastFocusedInput === 'ending') {
                    setEndingAddress(address);
                    setEndingPoint({ address, location: { lat, lng } });
                    setMapCenter({ lat, lng });
                }
            },
            (error) => {
                console.error(error);
            }
        );
    };

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setCurrentLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            }, (error) => {
                console.error("Error getting the geolocation: ", error);
            });
        }
    }, []);
    const addLocationControl = (map) => {
        const locationControlDiv = document.createElement('div');
        createRoot.render(<LocationButton map={map} />, locationControlDiv);
        map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(locationControlDiv);
    };
    const LocationButton = ({ map }) => {
        const goToCurrentLocation = () => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    const currentLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    map.setCenter(currentLocation);
                }, (error) => {
                    console.error("Error fetching geolocation: ", error);
                });
            }
        };

        return (
            <img src={location} onClick={goToCurrentLocation} alt="location" className="location" style={{ cursor: 'pointer', position: 'relative', right: '20px' }} />

        );
    };
    useEffect(() => {
        const calculateRoute = async () => {
            if (startingPoint && endingPoint) {
                const directionsService = new window.google.maps.DirectionsService();
                const results = await directionsService.route({
                    origin: startingPoint.location,
                    destination: endingPoint.location,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                });
                setDirectionsResponse(results);
            }
        };
        calculateRoute();
    }, [startingPoint, endingPoint]);
    const handleOnSubmit = async (values) => {
        if (window.google && values.startingPoint && values.endingPoint) {
            const directionsService = new window.google.maps.DirectionsService();
            const results = await directionsService.route({
                origin: values.startingPoint,
                destination: values.endingPoint,
                travelMode: window.google.maps.TravelMode.DRIVING,
            });
            setDirectionsResponse(results);
        } else {
            setDirectionsResponse(null);
        }
    };
    const [startingAutocomplete, setStartingAutocomplete] = useState(null);
    const [endingAutocomplete, setEndingAutocomplete] = useState(null);
    const onAutocompletePlaceChanged = (type) => {
        const autocomplete = type === 'starting' ? startingAutocomplete : endingAutocomplete;
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            if (!place.geometry) {
                console.error("No location found for input: ", type);
                return;
            }
            const location = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
            };
            if (type === 'starting') {
                if (place.formatted_address.trim() === '') {
                    setStartingPoint(null);
                } else {
                    setStartingPoint({ address: place.formatted_address, location });
                }
            } else {
                if (place.formatted_address.trim() === '') {
                    setEndingPoint(null);
                } else {
                    setEndingPoint({ address: place.formatted_address, location });
                }
            }
        } else {
            console.error("Autocomplete not loaded for input: ", type);
        }
        if (!startingPoint && !endingPoint) {
            setDirectionsResponse(null);
        }
    };
    const disabledDate = current => {
        return current && current < moment().startOf('day');
    };


    const renderContent = () => {
        return (
            <>
                <>
                    {isTaskAssigned ? (
                        <Result
                            status="success"
                            title="Successfully Assigned Task!"
                            subTitle="The task has been successfully assigned. This message will close automatically."
                        />
                    ) : (
                        <>
                            <Card
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Assign User Survey Task</span>
                                        <div className="flex items-center" style={{ position: 'relative', width: 250, height: 45 }}>
                                            <MdAdminPanelSettings style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }} />
                                            <Input
                                                disabled={true}
                                                value={selectedAdmin ? `Assigne by ${selectedAdmin.name}` : ''}
                                                style={{ paddingLeft: 35, width: '100%', height: '100%', borderRadius: 20 }}
                                            />
                                        </div>
                                    </div>
                                }
                                bordered={false}
                                style={{ width: '100%' }}
                            >
                                <Flex gap="small">
                                    <div
                                        id="scrollableDiv"
                                        style={{
                                            height: 520,
                                            width: 320,
                                            backgroundColor: '#ededed',
                                            overflow: 'hidden',
                                            padding: '16px',
                                            border: '1px solid rgba(140, 140, 140, 0.35)',
                                        }}
                                    >
                                        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Select a user for this task</div>
                                        <input
                                            type="text"
                                            placeholder="   Search for a users"
                                            style={{ width: '100%', height: 30, borderRadius: 5, marginBottom: '8px' }}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <div
                                            style={{
                                                overflowY: 'auto',
                                                maxHeight: '240px',
                                            }}
                                        >
                                            {selectedUser.value ? (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '10px',
                                                        backgroundColor: '#f0f0f0',
                                                    }}
                                                >
                                                    <MdPermContactCalendar />{selectedUser.label}
                                                </div>
                                            ) : (
                                                <List size="small">
                                                    {filteredUsers.map((user, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                padding: '10px',
                                                                cursor: 'pointer',
                                                                backgroundColor: 'transparent',
                                                            }}
                                                            onClick={() => setSelectedUser(user)}
                                                        >
                                                            <MdPermContactCalendar />{user.label}
                                                        </div>
                                                    ))}
                                                </List>
                                            )}
                                        </div>
                                        {selectedUser.value && (
                                            <button
                                                style={{
                                                    marginTop: '10px',
                                                    color: '#007bff',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setSelectedUser({})}
                                            >
                                                Change User
                                            </button>
                                        )}
                                    </div>
                                    <div
                                        id="taskdiv"
                                        style={{
                                            height: 520,
                                            flexGrow: 1,
                                            overflow: 'auto',
                                            padding: '0px 16px',

                                        }}
                                    >
                                        <Flex gap="small" vertical>
                                            <div className="p-field">
                                                <label htmlFor="roadIds" style={{ display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                                    Select Road IDs for AI Processing
                                                </label>
                                                {loadingRoadIds ? (
                                                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading Road IDs...</div>
                                                ) : (
                                                    <Select
                                                        mode="multiple"
                                                        id="roadIds"
                                                        placeholder="Select one or more Road IDs"
                                                        value={selectedRoadIds}
                                                        onChange={(values) => setSelectedRoadIds(values)}
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        showSearch
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            option.children.toLowerCase().includes(input.toLowerCase())
                                                        }
                                                    >
                                                        {availableRoadIds.map(road => (
                                                            <Select.Option key={road.road_id} value={road.road_id}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{road.road_id}</span>
                                                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                                                                        {road.road_name} ({road.video_count} videos)
                                                                    </span>
                                                                </div>
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                )}
                                                {selectedRoadIds.length > 0 && (
                                                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e6f7ff', borderRadius: '4px', fontSize: '13px' }}>
                                                        <strong>{selectedRoadIds.length}</strong> Road ID{selectedRoadIds.length > 1 ? 's' : ''} selected for AI processing
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-field">
                                                <label htmlFor="task" style={{ display: 'block', fontSize: '16px', fontWeight: '600' }}>Task Title</label>
                                                <TextArea
                                                    id="task"
                                                    value={task}
                                                    autoSize={{ minRows: 1, maxRows: 4 }}
                                                    onChange={(e) => setTask(e.target.value)}
                                                    style={{ width: '100%', padding: '0.2rem', borderColor: '#007bff', borderWidth: '1px', borderStyle: 'solid' }}
                                                />
                                            </div>
                                            <div className="p-field">
                                                <label htmlFor="description" style={{ display: 'block', fontSize: '16px', fontWeight: '600' }}>Task Description</label>
                                                <TextArea
                                                    id="description"
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    autoSize={{ minRows: 4, maxRows: 6 }}
                                                    style={{ width: '100%', padding: '0.2rem', borderColor: '#007bff', borderWidth: '1px', borderStyle: 'solid' }}
                                                />
                                            </div>
                                            <div className="p-field">
                                                <label htmlFor="date" style={{ display: 'block', fontSize: '16px', fontWeight: '600' }}>Due Date</label>
                                                <DatePicker
                                                    id="date"
                                                    value={dueDate}
                                                    onChange={(date, dateString) => setDueDate(date)}
                                                    style={{ width: '100%', padding: '0.2rem', borderColor: '#007bff', borderWidth: '1px', borderStyle: 'solid' }}
                                                    disabledDate={disabledDate}
                                                />
                                            </div>
                                            <div className="p-field">
                                                <label htmlFor="shift" style={{ display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Work Shift</label>
                                                <Radio.Group
                                                    id="shift"
                                                    value={shift}
                                                    onChange={(e) => setShift(e.target.value)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Radio.Button value="day" style={{ width: '48%', textAlign: 'center' }}>
                                                        🌅 Day Shift
                                                    </Radio.Button>
                                                    <Radio.Button value="night" style={{ width: '48%', textAlign: 'center', marginLeft: '4%' }}>
                                                        🌙 Night Shift
                                                    </Radio.Button>
                                                </Radio.Group>
                                            </div>
                                        </Flex>
                                    </div>
                                </Flex>
                                {error && (<Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 1 }} />)}
                            </Card>
                        </>
                    )}
                </>
            </>
        );
    };
    const footerContent = () => {
        return [
            <div key="button-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button key="cancel" danger onClick={() => { onHide(); resetForm(); }} ghost>Cancel</Button>
                <Button key="assign" type="primary" onClick={assignTask} icon={<CheckCircleTwoTone />} style={{ background: '#1967D2' }}>Submit for AI Processing</Button>
            </div>
        ];
    }
    return (
        <Modal
            centered
            closable={false}
            open={open}
            onCancel={onHide}
            footer={footerContent()}
            width="90vw"
            modal
        >
            {renderContent()}
        </Modal>
    );
};

export default CreateAppTask